import type { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../../interfaces/form.interface';
import { InputType, type DropdownData } from '../../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';
import type { ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';

export interface ReactiveFormPatchFacadeConfiguration<T> {
  readonly getForm: () => Form<T> | null;
  readonly getModel: () => FormGroup | null;
  readonly markNextModelChangeOrigin: (origin: ReactiveChangeOrigin) => void;
  readonly markForCheck: () => void;
}

export interface ReactiveFormExternalPatchOptions {
  readonly emit: boolean;
}

/**
 * Owns external model mutations that come from parent components.
 *
 * The component still owns dependency injection and rendering, while this facade keeps backend-id normalization and
 * patch-origin propagation in one testable place.
 */
export class ReactiveFormPatchFacade<T> {
  constructor(private readonly configuration: ReactiveFormPatchFacadeConfiguration<T>) {}

  public updateForm(data: Record<string, unknown>, options: ReactiveFormExternalPatchOptions): void {
    const model = this.configuration.getModel();

    if (!model) {
      return;
    }

    if (options.emit) {
      this.configuration.markNextModelChangeOrigin('externalPatch');
    }

    model.patchValue(this.normalizeExternalPatchData(data), { emitEvent: options.emit });
    this.configuration.markForCheck();
  }

  /**
   * Parent patches may contain backend ids while dropdown controls store canonical option values.
   */
  public normalizeExternalPatchData(data: Record<string, unknown>): Record<string, unknown> {
    const form = this.configuration.getForm();
    const normalizedData = { ...data };

    if (!form) {
      return normalizedData;
    }

    for (const [fieldName, value] of Object.entries(data)) {
      const field = this.findFieldByName(form, fieldName);

      if (field?.input?.type === InputType.SWITCH) {
        normalizedData[fieldName] = ReactiveFormValueUtils.normalizeSwitchValue(value);
        continue;
      }

      if (field?.input?.type === InputType.TWO_FIELDS_CONCAT) {
        this.normalizeTwoFieldsConcatPatchData(normalizedData, data, field, fieldName, value);
        continue;
      }

      if (!this.isNormalizableDropdownField(field)) {
        continue;
      }

      const dropdownData = field.input?.data as DropdownData<unknown> | undefined;
      const options = ReactiveFormValueUtils.getDropdownOptions(dropdownData);

      if (options.length === 0) {
        continue;
      }

      const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
      const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
      const optionsIndex = ReactiveFormValueUtils.createOptionsIndex(options, primaryKey, identityKeys);

      normalizedData[fieldName] = ReactiveFormValueUtils.remapValueWithOptions(
        value,
        options,
        primaryKey,
        ReactiveFormValueUtils.isMultiSelectField(field),
        true,
        optionsIndex,
        identityKeys,
      );
    }

    return normalizedData;
  }

  private normalizeTwoFieldsConcatPatchData(
    normalizedData: Record<string, unknown>,
    sourceData: Record<string, unknown>,
    field: FormField<T>,
    fieldName: string,
    value: unknown,
  ): void {
    const config = field.input?.twoFieldsConcatConfiguration;

    if (!config) {
      normalizedData[fieldName] = value ?? '';
      return;
    }

    const parentValue = String(value ?? '').trim();
    const [subField1Value, subField2Value] = ReactiveFormValueUtils.splitTwoFieldsConcat(
      parentValue,
      config.separator ?? 'x',
    );

    normalizedData[fieldName] = parentValue;

    if (config.subField1 && !(config.subField1 in sourceData)) {
      normalizedData[config.subField1] = subField1Value;
    }

    if (config.subField2 && !(config.subField2 in sourceData)) {
      normalizedData[config.subField2] = subField2Value;
    }
  }

  private isNormalizableDropdownField(field: FormField<T> | null): field is FormField<T> {
    return Boolean(field?.input?.dropdownConfiguration?.valuePrimaryKey || field?.input?.dropdownConfiguration?.controlValueKey);
  }

  private findFieldByName(form: Form<T>, fieldName: string): FormField<T> | null {
    for (const section of form.sections ?? []) {
      for (const field of section.fields ?? []) {
        if (field.name === fieldName) {
          return field;
        }
      }
    }

    return null;
  }
}
